import { House, HousePlusIcon, Landmark } from '@lucide/vue'

import { AssetWizardField, AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import {
  accountIdField,
  accountNameField,
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import { OWNERSHIP_STEP, SUMMARY_STEP, TAGS_STEP, VALUE_STEP } from '../utils/steps'
import { MORTGAGE_ACCOUNT_MODE_FIELD } from '../utils/const'
import { WizardAccountMode, WizardDefinition, WizardValues } from '../utils/types'

import MortgageStep from './steps/MortgageStep.vue'
import { realEstateWithFinancingSchema } from './schema'
import { create } from './create'
import { TagRef } from '@types'

const PROPERTY_TYPE = 'property'

const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Real Estate' },
  { tag_name: 'Investment Strategy', tag_value: 'Personal Property' },
]

/** Whether the mortgage step is on its "create a new mortgage" half. */
const isCreatingMortgage = (values: WizardValues) =>
  values[MORTGAGE_ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW

/**
 * One sentence, two linked records: the property and the borrowing that paid
 * for it. The only wizard with a bespoke step — the mortgage is either an
 * existing liability picked from the repository's own list, or a new one
 * named on the spot, the same either/or the ownership step itself asks.
 */
export const realEstateWithFinancingWizard: WizardDefinition = {
  flow: AssetWizardFlow.REAL_ESTATE_WITH_FINANCING,
  label: 'Real estate with financing',
  icon: HousePlusIcon,
  schema: realEstateWithFinancingSchema,
  steps: [
    OWNERSHIP_STEP,
    {
      key: 'property',
      label: 'Property',
      heading: 'What is this?',
      description: 'Houses, apartments, land and other real estate all go here.',
      fields: [
        assetNameField('Property name', 'Name this property'),
        descriptionField(),
      ],
    },
    {
      key: 'purchase',
      label: 'Purchase',
      heading: 'How much did you pay?',
      description: 'Record the purchase of your property.',
      fields: [
        amountField('Cost'),
        dateField('Date of purchase'),
        currencyField(),
      ],
    },
    {
      key: 'mortgage',
      label: 'Mortgage',
      heading: 'How is this financed?',
      description: 'Pick the mortgage from what this repository already has, or create a new one.',
      component: MortgageStep,
      fields: [
        accountIdField('Mortgage', values => !isCreatingMortgage(values), 'Find an existing mortgage'),
        accountNameField('Lender', 'Ex. Chase', isCreatingMortgage),
        {
          name: 'principal',
          label: 'Principal',
          control: 'number',
          placeholder: '0.00',
          required: true,
          aiField: AssetWizardField.PRINCIPAL,
        },
        {
          name: 'accountDescription',
          label: 'Description',
          control: 'text',
          placeholder: '—',
          aiField: AssetWizardField.ACCOUNT_DESCRIPTION,
        },
      ],
    },
    VALUE_STEP,
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'property',
      label: 'Property',
      icon: House,
      tone: 'brand',
      transactionType: 'add position',
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
    {
      key: 'mortgage',
      label: 'Mortgage',
      icon: Landmark,
      tone: 'neutral',
      transactionType: 'add liability',
      link: 'financed by',
      heading: 'accountName',
      fields: ['principal', 'accountDescription'],
    },
  ],
  constants: { assetType: PROPERTY_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
